import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

let reactPlugin = null;
let appInsights = null;

/**
 * Normalize the stored value into a valid Application Insights connection
 * string. Existing env files store a bare GUID, but the SDK requires the
 * `InstrumentationKey=` prefix when using connectionString.
 */
export const toConnectionString = value => {
  if (!value) return value;
  const str = String(value).trim();
  return str.startsWith('InstrumentationKey=') || str.startsWith('Endpoint=')
    ? str
    : `InstrumentationKey=${str}`;
};

/**
 * App Insights custom properties must be string/number/boolean. Objects and
 * arrays are JSON-stringified; null/undefined become empty strings.
 */
const sanitizeProperties = properties => {
  const result = {};
  if (!properties) return result;
  Object.entries(properties).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      result[key] = '';
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = value;
    } else {
      try {
        result[key] = JSON.stringify(value);
      } catch {
        result[key] = '[unserializable]';
      }
    }
  });
  return result;
};

/**
 * Create the App Insights Telemetry Service
 * @return {{reactPlugin: ReactPlugin, appInsights: Object, initialize: Function}} - Object
 */
const createTelemetryService = () => {
  /**
   * Initialize the Application Insights class
   * @param {string} instrumentationKey - Application Insights Instrumentation Key or connection string
   * @param {Object} browserHistory - client's browser history, supplied by the withRouter HOC
   * @param {string} [environment] - deployment environment (development, beta, production, etc.)
   * @return {void}
   */
  const initialize = (instrumentationKey, browserHistory, environment) => {
    if (!browserHistory) {
      throw new Error('Could not initialize Telemetry Service');
    }
    if (!instrumentationKey) {
      throw new Error(
        'Instrumentation key not provided in ./src/telemetry-provider.jsx'
      );
    }

    reactPlugin = new ReactPlugin();

    appInsights = new ApplicationInsights({
      config: {
        connectionString: toConnectionString(instrumentationKey),
        maxBatchInterval: 0,
        disableFetchTracking: false,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: browserHistory
          }
        }
      }
    });

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer(envelope => {
      const baseData = envelope?.baseData || {};
      const name = baseData.name || baseData.target || '';

      // Drop dependency telemetry for the connection-status polling endpoint.
      // It fires every 5 seconds and only creates noise/cost in Application Insights.
      if (
        envelope.baseType === 'RemoteDependencyData' &&
        name.includes('request.ashx')
      ) {
        return false;
      }

      if (environment) {
        baseData.properties = {
          ...baseData.properties,
          environment
        };
      }
      envelope.baseData = baseData;
    });
  };

  return { reactPlugin, appInsights, initialize };
};

export const ai = createTelemetryService();
export const getAppInsights = () => appInsights;

export const AppInsightsClient = ApplicationInsights;

/**
 * Send a trace log to Application Insights if it has been initialized.
 * Use this for diagnostic logging that should not spam the user's console.
 *
 * @param {string} message
 * @param {Object} [properties]
 * @param {number} [severityLevel] 0=Verbose,1=Information,2=Warning,3=Error,4=Critical
 */
export const trackTrace = (message, properties = {}, severityLevel = 1) => {
  if (!appInsights || typeof appInsights.trackTrace !== 'function') return;
  appInsights.trackTrace({
    message,
    severityLevel,
    properties: sanitizeProperties(properties)
  });
};

/**
 * Send an exception to Application Insights if it has been initialized.
 *
 * @param {Error} exception
 * @param {Object} [properties]
 */
export const trackException = (exception, properties = {}) => {
  if (!appInsights || typeof appInsights.trackException !== 'function') return;
  appInsights.trackException({
    exception,
    properties: sanitizeProperties(properties)
  });
};
